import React, { useCallback, useEffect, useLayoutEffect, useState } from 'react'
import Form from 'react-bootstrap/Form'
import FormGroup from 'react-bootstrap/FormGroup'
import FormLabel from 'react-bootstrap/FormLabel'
import FormControl from 'react-bootstrap/FormControl'
import Button from 'react-bootstrap/Button'
import { useSelector, useDispatch } from 'react-redux'
import { useParams, useNavigate } from 'react-router-dom'
import { APKEditViewProps } from './types'
import Select from 'app/components/Select'
import { RootState } from 'app/state/types'
import { APKUploadView } from './APKUploadView'
import RadioGroup from 'app/components/RadioGroup'
import { checkApkVersionCode, getApp } from 'app/services/client'
import {
    useTokenHeaderConfig,
    makeTokenHeaderConfig,
} from 'app/services/tokenConfig'
import { handleServerAPIError } from 'app/services/utils'
import { setApp } from 'app/state/appSlice'
import { APP_ROUTES } from 'app/components/AppRouter/routes'

export const APKEditableForm = (props: APKEditViewProps) => {
    const [isValid, setIsValid] = useState<boolean>(false)
    const [arc, setArc] = useState(0)
    const [isOpenUpload, setIsOpenUpload] = useState<boolean>(false)
    const {
        apk,
        onApkChange,
        onApkSelectChange,
        onSubmit,
        isEdit = false,
        onApkArcChange,
    } = props
    const { appStatuses, app } = useSelector(
        (state: RootState) => state.appSlice,
    )
    const [token] = useTokenHeaderConfig()
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const { appid } = useParams()

    const appArc = [
        {
            label: '32-bit',
            value: 0,
        },
        {
            label: '64-bit',
            value: 1,
        },
    ]

    useEffect(() => {
        if (apk.is64bit != undefined) {
            setArc(apk.is64bit ? 1 : 0)
        }
    }, [apk.is64bit])

    useLayoutEffect(() => {
        if (appid) {
            getApp(appid)
                .then(res => {
                    dispatch(setApp(res.data))
                })
                .catch(e => handleServerAPIError(e, dispatch, navigate))
        }
    }, [])

    const onBackClickHandler = useCallback(() => {
        navigate(APP_ROUTES.APP_DETAILS.replace(':id', app.id.toString()))
    }, [app])

    return (
        <>
            <APKUploadView
                appId={apk?.appId?.toString() || ''}
                apkId={apk?.id?.toString() || ''}
                isOpen={isOpenUpload}
                toggle={() => setIsOpenUpload(false)}
            />
            <Form className="m-3">
                <Button
                    className="mb-2"
                    variant="outline-success"
                    onClick={onBackClickHandler}>
                    Back to view
                </Button>
                <h1>{`App name: ${app.appName}`}</h1>
                <h2>{`Package name: ${app.packageName}`}</h2>
                <FormGroup className="mb-3">
                    <FormLabel>Version name</FormLabel>
                    <FormControl
                        name="versionName"
                        value={apk.versionName || ''}
                        onChange={onApkChange}
                        placeholder="1.0.0"
                    />
                </FormGroup>
                <FormGroup className="mb-3">
                    <FormLabel>Version code</FormLabel>
                    <FormControl
                        isInvalid={!isValid}
                        isValid={isValid}
                        name="versionCode"
                        value={apk.versionCode || ''}
                        onChange={onApkChange}
                        placeholder="1"
                        onBlur={event => {
                            if (!isEdit && appid) {
                                const data = event.target.value
                                if (data) {
                                    checkApkVersionCode(
                                        appid,
                                        data,
                                        makeTokenHeaderConfig(token),
                                    )
                                        .then(res => {
                                            setIsValid(!res.data)
                                        })
                                        .catch(e =>
                                            handleServerAPIError(
                                                e,
                                                dispatch,
                                                navigate,
                                            ),
                                        )
                                } else {
                                    setIsValid(false)
                                }
                            }
                        }}
                    />
                </FormGroup>
                <FormGroup className="mb-3">
                    <FormLabel>Status</FormLabel>
                    <Select
                        id="apkStatusLookupId"
                        name="apkStatusLookupId"
                        value={apk.apkStatusLookupId || ''}
                        data={appStatuses
                            .map(status => ({
                                ...status,
                                name: status.status,
                                value: status.id,
                            }))
                            .sort((cat1, cat2) =>
                                cat1.status.localeCompare(cat2.status),
                            )}
                        onChange={onApkSelectChange}
                    />
                </FormGroup>
                <FormGroup className="mb-3">
                    <FormLabel>What's new?</FormLabel>
                    <FormControl
                        name="apkWhatsNew"
                        value={apk.apkWhatsNew || ''}
                        onChange={onApkChange}
                        type="textarea"
                        placeholder="Example feature was added."
                    />
                </FormGroup>
                <RadioGroup
                    data={appArc}
                    name="is64bit"
                    selectedValue={arc}
                    updateState={onApkArcChange}
                />
                {isEdit && (
                    <Button
                        variant="outline-secondary"
                        onClick={() => setIsOpenUpload(true)}>
                        Upload APK
                    </Button>
                )}
                <Button
                    variant="success"
                    disabled={!isEdit && !isValid}
                    style={isEdit ? { marginLeft: '10px' } : {}}
                    onClick={onSubmit}>
                    {isEdit ? 'Save' : 'Create'}
                </Button>
            </Form>
        </>
    )
}
